import { useCallback, useEffect } from "react";

import { useToast } from "../../../../components";
import {
  TaskRunQuery,
  TaskRunStatus,
  useCompleteTaskRunMutation,
  useTaskRunByTargetQuery,
  useTaskRunQuery,
} from "../../../graphql";

type UseCompleteTaskReturn = {
  runCompleteTask: (configuration: { [key: string]: any } | null) => void;
  taskRun: TaskRunQuery["taskRun"];
  errorMessage: string | undefined;
  loading: boolean;
};

/** Dispatches a task then polls for its completion, and checks whether it was previously completed */
const useCompleteTask = (
  taskDefinitionId: string,
  input: { [key: string]: any },
  pollInterval = 10000
): UseCompleteTaskReturn => {
  const [completeTask, completeTaskMutation] = useCompleteTaskRunMutation();
  const toast = useToast();

  const runCompleteTask = useCallback(
    (configuration: { [key: string]: any } | null) => {
      completeTask({
        variables: {
          id: taskDefinitionId,
          taskInputJson: JSON.stringify(input),
          runConfigurationJson: configuration
            ? JSON.stringify(configuration)
            : null,
        },
        onError: (error) => {
          toast({
            title: "Error trying to run task",
            description: error.message,
            status: "error",
          });
        },
      });
    },
    [completeTask, taskDefinitionId, input, toast]
  );

  // The task may already have been run in a prior session, check it first.
  const previousTaskRunQuery = useTaskRunByTargetQuery({
    variables: {
      taskDefinitionId,
      userId: input.user_id,
      candidateId: input.candidate_id,
      positionId: input.position_id,
    },
    fetchPolicy: "cache-and-network",
  });

  // Once called, ignore the previous task run
  const taskRunId = completeTaskMutation.called
    ? completeTaskMutation.data?.completeTaskRun?.taskRun?.id
    : previousTaskRunQuery.data?.taskRunByTarget?.id;

  const taskRunQuery = useTaskRunQuery({
    variables: {
      id: taskRunId || "",
    },
    pollInterval,
    skip: !taskRunId,
  });

  // Stop polling when the task is completed or failed
  useEffect(() => {
    const taskStatus = taskRunQuery.data?.taskRun?.status;
    if (
      taskRunQuery.error ||
      (taskStatus &&
        [
          TaskRunStatus.Completed,
          TaskRunStatus.CompletedPreview,
          TaskRunStatus.Failed,
        ].includes(taskStatus))
    ) {
      taskRunQuery.stopPolling();
    }
  }, [
    taskRunQuery.data?.taskRun?.status,
    taskRunQuery.error,
    taskRunQuery.stopPolling,
  ]);

  const error = completeTaskMutation.called
    ? completeTaskMutation.error
    : taskRunQuery.error;

  const taskRun = taskRunQuery.data?.taskRun;

  return {
    runCompleteTask,
    taskRun,
    errorMessage: error?.message,
    loading: Boolean(
      // Show loading as soon as user hits run button
      (completeTaskMutation.called &&
        !taskRun &&
        !completeTaskMutation.error) ||
        // Or, once there is a task from past or current, show its loading status
        (taskRun &&
          (!taskRun.status || taskRun.status === TaskRunStatus.InProgress))
    ),
  };
};

export default useCompleteTask;
